import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown, Search } from 'lucide-react';
import { cn } from '@/utils/cn';
import type { ExperienceOption } from './ExperienceQuestionsCard';
import { ToWords } from 'to-words';
import { ensureFieldVisibleOnMobile } from '@/utils/mobileFieldScroll';

export type ExperienceDetailsAnswers = {
  firstPaidGigYear: string | null;
  eventAbout: string | null;
  performanceLocation: string | null;
  firstClientName: string;
  paymentMethod: string | null;
  hasPaymentProof: string | null;
  paymentAmount: string;
  performanceDurationMins: string;
};

const PAYMENT_METHOD_OPTIONS: ExperienceOption[] = [
  { value: 'cash', label: 'Cash' },
  { value: 'bank_transfer', label: 'Bank transfer' },
  { value: 'upi', label: 'UPI' },
  { value: 'cheque', label: 'Cheque' },
];

const PAYMENT_PROOF_OPTIONS: ExperienceOption[] = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

const iosEasing: [number, number, number, number] = [0.25, 0.1, 0.25, 1];

const questionMotion = {
  initial: { height: 0, opacity: 0 },
  animate: { height: 'auto', opacity: 1 },
  exit: { height: 0, opacity: 0 },
};

export function getVisibleQuestionCount(value: ExperienceDetailsAnswers): number {
  const completed = [
    !!value.firstPaidGigYear,
    !!value.eventAbout,
    !!value.performanceLocation?.trim(),
    !!value.firstClientName.trim(),
    !!value.paymentMethod,
    !!value.hasPaymentProof,
    !!value.paymentAmount.trim(),
    isValidPerformanceDurationMins(value.performanceDurationMins),
  ];
  const firstIncomplete = completed.findIndex((c) => !c);
  return firstIncomplete === -1 ? 8 : firstIncomplete + 1;
}

export const TOTAL_EXPERIENCE_QUESTIONS = 8;

export function getFilledQuestionCount(value: ExperienceDetailsAnswers): number {
  return [
    !!value.firstPaidGigYear,
    !!value.eventAbout,
    !!value.performanceLocation?.trim(),
    !!value.firstClientName.trim(),
    !!value.paymentMethod,
    !!value.hasPaymentProof,
    !!value.paymentAmount.trim(),
    isValidPerformanceDurationMins(value.performanceDurationMins),
  ].filter(Boolean).length;
}

export const MAX_PAYMENT_AMOUNT = 999_999;
export const MAX_PAYMENT_DIGITS = 6;

export const MIN_PERFORMANCE_DURATION_MINS = 10;
export const MAX_PERFORMANCE_DURATION_MINS = 480;

export const isValidPerformanceDurationMins = (digits: string): boolean => {
  const n = Number(digits);
  return (
    Number.isFinite(n) &&
    n >= MIN_PERFORMANCE_DURATION_MINS &&
    n <= MAX_PERFORMANCE_DURATION_MINS
  );
};

export const formatPaymentAmount = (digits: string): string => {
  if (!digits) return '';
  if (digits.length < 5) return digits;
  return Number(digits).toLocaleString('en-IN');
};

const paymentAmountToWords = new ToWords({
  localeCode: 'en-IN',
  converterOptions: {
    currency: true,
    ignoreDecimal: true,
    doNotAddOnly: true,
  },
});

export const formatPaymentAmountInWords = (digits: string): string | null => {
  if (!digits || digits === '0') return null;
  const amount = Number(digits);
  if (!Number.isFinite(amount) || amount <= 0 || amount > MAX_PAYMENT_AMOUNT) return null;
  try {
    return paymentAmountToWords.convert(amount, { currency: true });
  } catch {
    return null;
  }
};

const stripNonDigits = (value: string) => value.replace(/\D/g, '');

type ExperienceDropdownProps = {
  value: string | null;
  onChange: (value: string) => void;
  placeholder: string;
  options: ExperienceOption[];
  searchable?: boolean;
  searchPlaceholder?: string;
  disabled?: boolean;
};

const ExperienceDropdown: React.FC<ExperienceDropdownProps> = ({
  value,
  onChange,
  placeholder,
  options,
  searchable = false,
  searchPlaceholder = 'Search...',
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});

  const updateMenuPosition = () => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const vv = window.visualViewport;
    const viewportTop = vv?.offsetTop ?? 0;
    const viewportHeight = vv?.height ?? window.innerHeight;
    const viewportBottom = viewportTop + viewportHeight;
    const availableBelow = viewportBottom - rect.bottom - 12;
    const availableAbove = rect.top - viewportTop - 12;
    const shouldOpenUp = availableBelow < 180 && availableAbove > availableBelow;
    const maxHeight = Math.min(300, Math.max(120, shouldOpenUp ? availableAbove : availableBelow));

    setMenuStyle({
      position: 'fixed',
      left: rect.left,
      width: rect.width,
      top: shouldOpenUp ? undefined : rect.bottom + 8,
      bottom: shouldOpenUp ? window.innerHeight - rect.top + 8 : undefined,
      maxHeight,
      zIndex: 1000,
      pointerEvents: 'auto',
    });
  };

  useEffect(() => {
    if (!open) return;

    updateMenuPosition();

    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      setOpen(false);
    };

    const onWindowChange = () => updateMenuPosition();

    const onScrollCapture = (event: Event) => {
      const target = event.target as Node | null;
      if (target && menuRef.current?.contains(target)) return;
      updateMenuPosition();
    };

    document.addEventListener('mousedown', onPointerDown);
    window.addEventListener('resize', onWindowChange);
    window.addEventListener('scroll', onScrollCapture, true);
    window.visualViewport?.addEventListener('resize', onWindowChange);
    window.visualViewport?.addEventListener('scroll', onWindowChange);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('resize', onWindowChange);
      window.removeEventListener('scroll', onScrollCapture, true);
      window.visualViewport?.removeEventListener('resize', onWindowChange);
      window.visualViewport?.removeEventListener('scroll', onWindowChange);
    };
  }, [open]);

  const selected = useMemo(
    () => options.find((option) => option.value === value) ?? null,
    [options, value]
  );

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((option) => {
      const haystack = `${option.label} ${option.subtitle ?? ''}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [options, query]);

  return (
    <div className="relative" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          setOpen((prev) => {
            const next = !prev;
            if (next) {
              setQuery('');
              window.setTimeout(() => {
                if (triggerRef.current) {
                  ensureFieldVisibleOnMobile(triggerRef.current, { extraBottomInset: 88 });
                }
              }, 0);
            }
            return next;
          });
        }}
        className="w-full rounded-2xl bg-[#141416] border border-white/10 px-4 py-3 text-left flex items-center justify-between gap-3 disabled:opacity-60"
      >
        <span
          className={cn(
            'text-base font-medium truncate',
            selected ? 'text-white' : 'text-[#8E8E93]'
          )}
          title={selected ? selected.label : placeholder}
        >
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown className={cn('h-5 w-5 shrink-0 text-[#8E8E93] transition-transform', open && 'rotate-180')} />
      </button>

      {open && typeof document !== 'undefined' && createPortal(
        <div
          ref={menuRef}
          style={menuStyle}
          className="rounded-xl border border-white/10 bg-[#141416] shadow-2xl overflow-hidden pointer-events-auto"
          onWheelCapture={(e) => e.stopPropagation()}
          onTouchMoveCapture={(e) => e.stopPropagation()}
        >
          {searchable && (
            <div className="p-2 border-b border-white/10 bg-[#141416]">
              <div className="flex items-center gap-2 rounded-md bg-[#1C1C1E] px-3 py-2 border border-white/10">
                <Search className="h-4 w-4 text-[#8E8E93]" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="w-full bg-transparent text-sm text-white placeholder:text-[#8E8E93] outline-none"
                />
              </div>
            </div>
          )}

          <div
            className="overflow-y-auto py-1"
            style={{
              maxHeight:
                typeof menuStyle.maxHeight === 'number'
                  ? Math.max(120, menuStyle.maxHeight - (searchable ? 56 : 0))
                  : menuStyle.maxHeight,
              WebkitOverflowScrolling: 'touch',
              overscrollBehavior: 'contain',
              touchAction: 'pan-y',
            }}
          >
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-[#8E8E93]">No results found</div>
            ) : (
              filteredOptions.map((option) => (
                <button
                  key={`${option.value}-${option.label}`}
                  type="button"
                  onClick={() => {
                    onChange(option.value);
                    setOpen(false);
                    setQuery('');
                  }}
                  className="w-full px-3 py-2.5 text-left hover:bg-white/5 transition-colors"
                >
                  <div className="text-sm text-white font-semibold">{option.label}</div>
                  {option.subtitle && <div className="text-xs text-[#8E8E93]">{option.subtitle}</div>}
                </button>
              ))
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

const QuestionBlock: React.FC<{ label: React.ReactNode; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="space-y-2">
    <p className="text-sm font-medium text-[#8E8E93] leading-snug break-words">{label}</p>
    {children}
  </div>
);

const AnimatedQuestion: React.FC<{
  index: number;
  staggerEnter?: boolean;
  questionRef: (el: HTMLDivElement | null) => void;
  children: React.ReactNode;
}> = ({ index, staggerEnter = false, questionRef, children }) => {
  const enterDelay = staggerEnter ? index * 0.04 : 0;

  return (
    <motion.div
      ref={questionRef}
      data-experience-question
      className="overflow-hidden"
      initial={questionMotion.initial}
      animate={questionMotion.animate}
      exit={questionMotion.exit}
      transition={{
        height: { duration: 0.35, ease: iosEasing, delay: enterDelay },
        opacity: { duration: 0.35, ease: iosEasing, delay: enterDelay },
      }}
    >
      {children}
    </motion.div>
  );
};

export const ExperienceDetailsForm: React.FC<{
  categoryLabel: string;
  value: ExperienceDetailsAnswers;
  onChange: (next: ExperienceDetailsAnswers) => void;
  eventTypeOptions: ExperienceOption[];
  resetKey?: number;
}> = ({
  categoryLabel,
  value,
  onChange,
  eventTypeOptions,
  resetKey,
}) => {
  const [displayCount, setDisplayCount] = useState(1);
  const [staggerEnter, setStaggerEnter] = useState(false);
  const questionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const formRootRef = useRef<HTMLDivElement | null>(null);
  const prevDisplayCountRef = useRef(1);

  const targetCount = useMemo(() => getVisibleQuestionCount(value), [value]);
  const filledCount = useMemo(() => getFilledQuestionCount(value), [value]);
  const paymentAmountWords = useMemo(
    () => formatPaymentAmountInWords(value.paymentAmount),
    [value.paymentAmount]
  );
  const progressPercent = (filledCount / TOTAL_EXPERIENCE_QUESTIONS) * 100;

  const yearOptions = useMemo<ExperienceOption[]>(() => {
    const now = new Date().getFullYear();
    return Array.from({ length: 31 }).map((_, idx) => {
      const year = String(now - idx);
      return { value: year, label: year };
    });
  }, []);

  const setQuestionRef = (index: number) => (el: HTMLDivElement | null) => {
    questionRefs.current[index] = el;
  };

  useEffect(() => {
    if (displayCount > prevDisplayCountRef.current) {
      const el = questionRefs.current[displayCount - 1];
      window.setTimeout(() => {
        if (el) ensureFieldVisibleOnMobile(el, { extraBottomInset: 88 });
      }, 380);
    }
    prevDisplayCountRef.current = displayCount;
  }, [displayCount]);

  useEffect(() => {
    if (resetKey == null) return;
    const visibleCount = getVisibleQuestionCount(value);
    setDisplayCount(visibleCount);
    prevDisplayCountRef.current = visibleCount;
    setStaggerEnter(visibleCount > 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  useEffect(() => {
    if (displayCount >= targetCount) return;
    const jump = targetCount - displayCount;
    if (jump > 1) {
      setStaggerEnter(true);
      setDisplayCount(targetCount);
      return;
    }
    setStaggerEnter(false);
    setDisplayCount((c) => Math.min(c + 1, targetCount));
  }, [displayCount, targetCount]);

  const inputClassName =
    'w-full rounded-2xl bg-[#141416] border border-white/10 px-4 py-3 text-base text-white placeholder:text-[#8E8E93] outline-none';

  return (
    <div ref={formRootRef} className="flex flex-col min-h-0 pt-1 pb-4">
      <motion.p
        className="text-sm text-[#FACC15] leading-snug break-words shrink-0"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: iosEasing }}
      >
        We need your Experience details starting from your 1<sup>st</sup> on-stage Performance as{' '}
        <span className="text-[#FACC15] font-medium">{categoryLabel}</span>
      </motion.p>

      <div className="mt-4 flex shrink-0 items-center gap-3 border-b border-white/5 pb-3">
        <span className="text-[11px] text-[#9CA3AF] tabular-nums shrink-0">
          Step {filledCount} of {TOTAL_EXPERIENCE_QUESTIONS}
        </span>
        <div
          className="flex-1 h-1 rounded-full bg-white/5 overflow-hidden"
          role="progressbar"
          aria-valuenow={filledCount}
          aria-valuemin={0}
          aria-valuemax={TOTAL_EXPERIENCE_QUESTIONS}
          aria-label="Experience questions progress"
        >
          <motion.div
            className="h-full rounded-full bg-gradient-to-r from-[#F33363] to-[#FACC15]"
            initial={false}
            animate={{ width: `${progressPercent}%` }}
            transition={{ duration: 0.35, ease: iosEasing }}
          />
        </div>
      </div>

      <div
        data-experience-questions-scroll
        className="mt-0 max-h-[min(48dvh,420px)] sm:max-h-[min(52dvh,460px)] overflow-y-auto overscroll-y-contain space-y-6 pt-4 pb-4 scrollbar-hide [overflow-scrolling:touch]"
      >
        <AnimatePresence mode="sync">
          {displayCount >= 1 && (
            <AnimatedQuestion key="q1" index={0} staggerEnter={staggerEnter} questionRef={setQuestionRef(0)}>
              <QuestionBlock
                label={
                  <>
                    When was your 1<sup>st</sup> PAID gig as {categoryLabel}?
                  </>
                }
              >
                <div className="relative">
                  <select
                    value={value.firstPaidGigYear ?? ''}
                    onChange={(e) =>
                      onChange({ ...value, firstPaidGigYear: e.target.value || null })
                    }
                    className="w-full appearance-none rounded-2xl bg-[#141416] border border-white/10 px-4 py-3 pr-10 text-base font-medium text-white outline-none"
                  >
                    <option value="" disabled>
                      Select year
                    </option>
                    {yearOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#8E8E93]" />
                </div>
              </QuestionBlock>
            </AnimatedQuestion>
          )}

          {displayCount >= 2 && (
            <AnimatedQuestion key="q2" index={1} staggerEnter={staggerEnter} questionRef={setQuestionRef(1)}>
              <QuestionBlock label="What was the Event about?">
                <ExperienceDropdown
                  value={value.eventAbout}
                  onChange={(selected) => onChange({ ...value, eventAbout: selected })}
                  placeholder="Select event type"
                  options={eventTypeOptions}
                />
              </QuestionBlock>
            </AnimatedQuestion>
          )}

          {displayCount >= 3 && (
            <AnimatedQuestion key="q3" index={2} staggerEnter={staggerEnter} questionRef={setQuestionRef(2)}>
              <QuestionBlock label="Where did you perform?">
                <input
                  value={value.performanceLocation ?? ''}
                  onChange={(e) =>
                    onChange({ ...value, performanceLocation: e.target.value || null })
                  }
                  placeholder="Enter city"
                  className={inputClassName}
                />
              </QuestionBlock>
            </AnimatedQuestion>
          )}

          {displayCount >= 4 && (
            <AnimatedQuestion key="q4" index={3} staggerEnter={staggerEnter} questionRef={setQuestionRef(3)}>
              <QuestionBlock
                label={
                  <>
                    What was the name of your 1<sup>st</sup> Client?
                  </>
                }
              >
                <input
                  value={value.firstClientName}
                  name="express-booking-first-client"
                  autoComplete="off"
                  data-lpignore="true"
                  onChange={(e) => onChange({ ...value, firstClientName: e.target.value })}
                  placeholder="Client / Brand Name"
                  className={inputClassName}
                />
              </QuestionBlock>
            </AnimatedQuestion>
          )}

          {displayCount >= 5 && (
            <AnimatedQuestion key="q5" index={4} staggerEnter={staggerEnter} questionRef={setQuestionRef(4)}>
              <QuestionBlock label="How did the Client pay you?">
                <ExperienceDropdown
                  value={value.paymentMethod}
                  onChange={(selected) => onChange({ ...value, paymentMethod: selected })}
                  placeholder="Select payment method"
                  options={PAYMENT_METHOD_OPTIONS}
                />
              </QuestionBlock>
            </AnimatedQuestion>
          )}

          {displayCount >= 6 && (
            <AnimatedQuestion key="q6" index={5} staggerEnter={staggerEnter} questionRef={setQuestionRef(5)}>
              <QuestionBlock label="Do you have proof of the payment received by you?">
                <ExperienceDropdown
                  value={value.hasPaymentProof}
                  onChange={(selected) => onChange({ ...value, hasPaymentProof: selected })}
                  placeholder="Select"
                  options={PAYMENT_PROOF_OPTIONS}
                />
              </QuestionBlock>
            </AnimatedQuestion>
          )}

          {displayCount >= 7 && (
            <AnimatedQuestion key="q7" index={6} staggerEnter={staggerEnter} questionRef={setQuestionRef(6)}>
              <QuestionBlock label="How much did you get paid?">
                <div className="space-y-2">
                  <div className="flex items-stretch rounded-2xl bg-[#141416] border border-white/10 overflow-hidden">
                    <div className="flex items-center px-4 py-3 border-r border-white/10 bg-[#1C1C1E]/60 shrink-0">
                      <span className="text-base font-semibold text-[#8E8E93]">₹</span>
                    </div>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={formatPaymentAmount(value.paymentAmount)}
                      onChange={(e) => {
                        const digits = stripNonDigits(e.target.value).slice(0, MAX_PAYMENT_DIGITS);
                        onChange({ ...value, paymentAmount: digits });
                      }}
                      placeholder="Enter amount"
                      className="flex-1 min-w-0 bg-transparent px-4 py-3 text-base text-white placeholder:text-[#8E8E93] outline-none border-0"
                    />
                  </div>
                  {paymentAmountWords && (
                    <p className="text-xs text-[#8E8E93] leading-snug break-words">
                      {paymentAmountWords}
                    </p>
                  )}
                </div>
              </QuestionBlock>
            </AnimatedQuestion>
          )}

          {displayCount >= 8 && (
            <AnimatedQuestion key="q8" index={7} staggerEnter={staggerEnter} questionRef={setQuestionRef(7)}>
              <QuestionBlock label="What was the duration of your performance?">
                <div className="space-y-2">
                  <div className="flex items-stretch rounded-2xl bg-[#141416] border border-white/10 overflow-hidden">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={value.performanceDurationMins}
                      onChange={(e) => {
                        const digits = stripNonDigits(e.target.value).slice(0, 3);
                        if (!digits) {
                          onChange({ ...value, performanceDurationMins: '' });
                          return;
                        }
                        const num = Number(digits);
                        if (num > MAX_PERFORMANCE_DURATION_MINS) {
                          onChange({
                            ...value,
                            performanceDurationMins: String(MAX_PERFORMANCE_DURATION_MINS),
                          });
                          return;
                        }
                        onChange({ ...value, performanceDurationMins: digits });
                      }}
                      className="flex-1 min-w-0 bg-transparent px-4 py-3 text-base text-white placeholder:text-[#8E8E93] outline-none border-0"
                    />
                    <div className="flex items-center px-4 py-3 border-l border-white/10 bg-[#1C1C1E]/60 shrink-0">
                      <span className="text-base font-semibold text-[#8E8E93]">mins</span>
                    </div>
                  </div>
                  <AnimatePresence initial={false}>
                    {value.performanceDurationMins.trim() &&
                      !isValidPerformanceDurationMins(value.performanceDurationMins) && (
                        <motion.p
                          key="duration-hint"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.22, ease: iosEasing }}
                          className="overflow-hidden text-xs text-[#8E8E93] leading-snug"
                        >
                          Enter duration between 10 and 480 mins
                        </motion.p>
                      )}
                  </AnimatePresence>
                </div>
              </QuestionBlock>
            </AnimatedQuestion>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
